import React, { ReactNode, useMemo } from "react";
import { NameAnd } from "@me8eon/record_utils";
import { DataHasType } from "@me8eon/data";
import { makeContextFor } from "@me8eon/context";
import { FeatureFlags, useFeatureFlags } from "@me8eon/featureflags";

export const debugDataWidgets = "dataWidget";


export type DataWidgetLayoutProps = {
    rootId?: string;
    children: ReactNode;
}
export type DataWidgetLayout = (props: DataWidgetLayoutProps) => ReactNode


export type DataWidgetProps<Data extends DataHasType> = {
    rootId: string;
    data: Data[]
    widgetName: string
    options: string[]
}
export type DataWidgetDisplayer = <Data extends DataHasType>(props: DataWidgetProps<Data>) => ReactNode;

export type DataWidgetWidth = "auto" | "small" | "medium" | "large";
export type DataWidget = {
    plugin: "datawidget"
    name: string
    display: DataWidgetDisplayer
    defaultWidth?: DataWidgetWidth
}

export type DataWidgets = NameAnd<DataWidget>

export const { Provider: RawDataWidgetProvider, use: useDataWidgets } = makeContextFor<DataWidgets, "dataWidgets">("dataWidgets");

export type DataWidgetProviderProps = {
    dataWidgets: (ff: FeatureFlags) => DataWidgets
    children: ReactNode
}

export function DataWidgetProvider({ dataWidgets, children }: DataWidgetProviderProps) {
    const [featureFlags] = useFeatureFlags();
    const widgets = useMemo(() => dataWidgets(featureFlags), [dataWidgets, featureFlags]);
    return <RawDataWidgetProvider dataWidgets={widgets}>{children}</RawDataWidgetProvider>;
}

export function orFilter(...fns: ((d: any) => boolean)[]) {
    return (d: any) => fns.some((fn) => fn(d));
}

export function andFilter(...fns: ((d: any) => boolean)[]) {
    return (d: any) => fns.every((fn) => fn(d));
}

export function fromType(index: string) {
    return (d: DataHasType) => d.type === index;
}

export function fromSource(source: string) {
    return (d: any) => d.source === source;
}

export function fromIndex(index: string) {
    return (d: any) => d.index === index;
}

export function fromIndicies(indicies: string[]) {
    return (d: any) => indicies.includes(d.index);
}

export function notFromindices(indicies: string[]) {
    return (d: any) => !indicies.includes(d.index);
}

export function notFromindicesAndHighScore(indicies: string[], cuttOff: number) {
    return (d: any) => !indicies.includes(d.index) && (d?._score || 0) > cuttOff;
}

